import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";

import { ScrollToTop } from "./components/ScrollToTop";
import { AnalyticsRouteTracker } from "./components/AnalyticsRouteTracker";
import { LocaleProvider } from "./components/LocaleProvider";
import LandingPage from "./pages/LandingPage";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { SiteRibbon } from "./components/SiteRibbon";
import ImportedPage from "./redesign/ImportedPage";
import importedManifest from "./redesign/manifest.json";
import { importedPageRoute } from "./redesign/routes";

// Lazy-load non-landing pages
const AnnotatePage = lazy(() => import("./pages/AnnotatePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AdvisorsPage = lazy(() => import("./pages/AdvisorsPage"));
const FAQsPage = lazy(() => import("./pages/FAQsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const JobsLandingPage = lazy(() => import("./pages/careers/JobsLandingPage"));
const ExpertsPage = lazy(() => import("./pages/careers/ExpertsPage"));
const RolesPage = lazy(() => import("./pages/careers/RolesPage"));
const BookDemoPage = lazy(() => import("./pages/BookDemoPage"));
const EthicalSupplierManagementPage = lazy(() => import("@/pages/EthicalSupplierManagementPage"));
const CodeOfConductPage = lazy(() => import("@/pages/CodeOfConductPage"));
const TraffickingAndExploitationStatementPage = lazy(() => import("@/pages/TraffickingAndExploitationStatementPage"));
const PrivacyStatementPage = lazy(() => import("@/pages/PrivacyStatementPage"));
const TermsConditionsPage = lazy(() => import("@/pages/TermsConditionsPage"));
const ArgusPage = lazy(() => import("@/pages/ArgusPage"));
const AutoRLPage = lazy(() => import("@/pages/AutoRLPage"));
const NewsAndViewsPage = lazy(() => import("@/pages/NewsAndViewsPage"));
const NewsArticlePage = lazy(() => import("@/pages/NewsArticlePage"));
const HallucinationReportPage = lazy(() => import("@/pages/HallucinationReportPage"));
const ArgusRLPage = lazy(() => import("@/pages/ArgusRLPage"));
const ArgusAIEvidencePage = lazy(() => import("@/pages/ArgusAIEvidencePage"));
const ChromeRedirect = lazy(() => import("@/pages/ChromeRedirect"));
const IndustryLegalPage = lazy(() => import("@/pages/IndustryLegalPage"));
const IndustryHealthcarePage = lazy(() => import("@/pages/IndustryHealthcarePage"));
const IndustryEducationPage = lazy(() => import("@/pages/IndustryEducationPage"));
const IndustryGeneralBusinessPage = lazy(() => import("@/pages/IndustryGeneralBusinessPage"));
const MediaKitPage = lazy(() => import("@/pages/MediaKitPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const DevelopersPage = lazy(() => import("@/pages/DevelopersPage"));
const JobsOpeningsPage = lazy(() => import("@/pages/JobsPage"));
const AIProofreaderPage = lazy(() => import("@/pages/AIProofreaderPage"));
const AIProofreaderWordPage = lazy(() => import("@/pages/AIProofreaderWordPage"));
const DPAPage = lazy(() => import("@/pages/DPAPage"));
const EULAPage = lazy(() => import("@/pages/EULAPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <SiteRibbon />
          <Toaster />
          <Sonner />
          <Router>
            <ScrollToTop />
            <AnalyticsRouteTracker />
            <Suspense fallback={<div className="min-h-screen" />}>
              <Routes>
                {/* Redirect root to /en */}
                <Route path="/" element={<Navigate to="/en" replace />} />

                {/* Isolated standalone landing — no locale, no nav */}
                <Route path="/argus-ai-evidence" element={<ArgusAIEvidencePage />} />

                {/* Standalone AI Proofreader landing */}
                <Route path="/AI-proofreader" element={<AIProofreaderPage />} />
                <Route path="/ai-proofreader" element={<AIProofreaderPage />} />
                <Route path="/ai-proofreader-word" element={<AIProofreaderWordPage />} />
                <Route path="/AI-proofreader-word" element={<AIProofreaderWordPage />} />

                {/* Chrome Web Store redirect */}
                <Route path="/chrome" element={<ChromeRedirect />} />


                {/* Locale-prefixed routes */}
                <Route path="/:locale" element={<LocaleProvider />}>
                  <Route index element={<ImportedPage page="index" />} />
                  {Object.keys(importedManifest).filter(page => page !== "index").map(page => (
                    <Route key={page} path={importedPageRoute(page)} element={<ImportedPage page={page as keyof typeof importedManifest} />} />
                  ))}
                  <Route path="aitraining" element={<AnnotatePage />} />
                  <Route path="annotate" element={<Navigate to="../aitraining" replace />} />
                  <Route path="faqs" element={<FAQsPage />} />
                  <Route path="jobs/openings" element={<JobsOpeningsPage />} />
                  <Route path="jobs/experts" element={<ExpertsPage />} />
                  <Route path="jobs/roles" element={<RolesPage />} />
                  <Route path="book-demo" element={<BookDemoPage />} />
                  <Route path="ethical-supplier-management" element={<EthicalSupplierManagementPage />} />
                  <Route path="code-of-conduct" element={<CodeOfConductPage />} />
                  <Route path="trafficking-and-exploitation-statement" element={<TraffickingAndExploitationStatementPage />} />
                  <Route path="privacy-statement" element={<PrivacyStatementPage />} />
                  <Route path="cookie-policy" element={<CookiePolicyPage />} />
                  <Route path="cookies" element={<Navigate to="../cookie-policy" replace />} />
                  <Route path="dpa" element={<DPAPage />} />
                  <Route path="eula" element={<EULAPage />} />
                  <Route path="terms-conditions" element={<TermsConditionsPage />} />
                  <Route path="autorl" element={<Navigate to="../argusrl" replace />} />
                  <Route path="autonomousrl" element={<Navigate to="../argusrl" replace />} />
                  <Route path="news" element={<NewsAndViewsPage />} />
                 <Route path="news/:slug" element={<NewsArticlePage />} />
                 <Route path="hallucination-report/archive" element={<HallucinationReportPage />} />
                  <Route path="news&more" element={<Navigate to="../news" replace />} />
                  <Route path="news&more/:slug" element={<Navigate to="../news" replace />} />
                  <Route path="industries/legal" element={<Navigate to="../legal" replace />} />
                  <Route path="industries/healthcare" element={<Navigate to="../healthcare" replace />} />
                  <Route path="industries/education" element={<Navigate to="../education" replace />} />
                  <Route path="industries/general-business" element={<Navigate to="../enterprise" replace />} />
                  <Route path="media-kit" element={<MediaKitPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="use-cases/*" element={<Navigate to="../pricing" replace />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                {/* Catch-all: redirect to /en */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <CookieConsentBanner />
          </Router>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
